import React from 'react';
import { Container, Row, Col, Badge } from 'react-bootstrap';
import { useThemeMode } from '../../ThemeContext';

// Import assets for HowItWorks section
import Step1 from '../../assets/how-step-1.png';
import Step2 from '../../assets/how-step-2.png';
import Step3 from '../../assets/how-step-3.png';

// Import dark theme images for all steps
import DarkChat from '../../assets/landing_page_images/Dark-Chat.jpg';
import DarkDocumentUploadFlow from '../../assets/landing_page_images/Dark-DocumentUploadFlow.png';
import DarkDashboard from '../../assets/landing_page_images/Dark-Dashboard.jpg';
import DarkManageUser from '../../assets/landing_page_images/Dark-ManageUser.png';

// Import light theme images for all steps
import LightDocumentUploadFlow from '../../assets/landing_page_images/Light-DocumentUploadFlow.png';
import LightChat from '../../assets/landing_page_images/Light-Chat.png';
import LightDashboard from '../../assets/landing_page_images/Light-Dashboard.png';
import LightManageUser from '../../assets/landing_page_images/Light-ManageUser.png';

function HowItWorks() {
  const { mode } = useThemeMode();

  return (
    <>
      {/* How It Works Section */}
      <section className="howitworks-section-bg">
        <Container>
          <div className="howitworks-header">
            <div className="howitworks-title">How Complimate Works</div>
            <div className="howitworks-subtitle">Complimate turns compliance into a simple, measurable workflow — from documents to audits, tasks, and accountability.</div>
          </div>
          {/* Step 1 */}
          <Row className="align-items-center mb-5 flex-wrap-reverse">
            <Col xs={12} md={6}>
              <div className="howitworks-step-info">
                <Badge className="howitworks-badge" bg="none">Step - 1</Badge>
                <div className="howitworks-step-content">
                  <div className="howitworks-step-title">Upload & Manage Your Documents</div>
                  <div className="howitworks-step-desc">Upload policies, procedures, and compliance records — all in one place.</div>
                  <div className="howitworks-checklist">
                    <div className="howitworks-checklist-row">
                      <span className="howitworks-chevron">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 16L14 12L10 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>Centralise WHS, Quality, Environment, and CoR documents.</span>
                    </div>
                    <div className="howitworks-checklist-row">
                      <span className="howitworks-chevron">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 16L14 12L10 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>Upload SWMS, risk assessments, registers, WHS Plans, policies, and procedures.</span>
                    </div>
                    <div className="howitworks-checklist-row">
                      <span className="howitworks-chevron">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 16L14 12L10 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>Built-in version control, expiry tracking, and instant search.</span>
                    </div>
                    <div className="howitworks-checklist-row">
                      <span className="howitworks-chevron">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 16L14 12L10 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>Secure, encrypted, and Australian-hosted.</span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={12} md={6} className="mb-4 mb-md-0">
              <div className="howitworks-image-col text-center">
                <img src={mode === 'dark' ? DarkDocumentUploadFlow : LightDocumentUploadFlow} alt="Step 1" className="howitworks-image img-fluid" />
              </div>
            </Col>
          </Row>
          {/* Step 2 */}
          <Row className="align-items-center mb-5 flex-wrap">
            <Col xs={12} md={6} className="order-md-2">
              <div className="howitworks-step-info">
                <Badge className="howitworks-badge" bg="none">Step - 2</Badge>
                <div className="howitworks-step-content">
                  <div className="howitworks-step-title">Ask the Compliance Assistant</div>
                  <div className="howitworks-step-desc">Instant answers from your documents and the law.</div>
                  <div className="howitworks-checklist">
                    <div className="howitworks-checklist-row">
                      <span className="howitworks-chevron">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 16L14 12L10 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>Ask any compliance question — answers are pulled directly from uploaded docs.</span>
                    </div>
                    <div className="howitworks-checklist-row">
                      <span className="howitworks-chevron">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 16L14 12L10 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>Cross-referenced with WHS Act, WHS Regulation, ISO standards, and Government requirements.</span>
                    </div>
                    <div className="howitworks-checklist-row">
                      <span className="howitworks-chevron">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 16L14 12L10 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>Draft policies & procedures in seconds with AI-powered generation.</span>
                    </div>
                    <div className="howitworks-checklist-row">
                      <span className="howitworks-chevron">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 16L14 12L10 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>Available 24/7 on desktop and mobile.</span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={12} md={6} className="order-md-1 mb-4 mb-md-0">
              <div className="howitworks-image-col text-center">
                <img src={mode === 'dark' ? DarkChat : LightChat} alt="Step 2" className="howitworks-image img-fluid" />
              </div>
            </Col>
          </Row>
          {/* Step 3 */}
          <Row className="align-items-center mb-5 flex-wrap-reverse">
            <Col xs={12} md={6}>
              <div className="howitworks-step-info">
                <Badge className="howitworks-badge" bg="none">Step - 3</Badge>
                <div className="howitworks-step-content">
                  <div className="howitworks-step-title">Track Alerts, Projects & Corrective Actions</div>
                  <div className="howitworks-step-desc">Never miss a gap, deadline, or regulator requirement.</div>
                  <div className="howitworks-checklist">
                    <div className="howitworks-checklist-row">
                      <span className="howitworks-chevron">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 16L14 12L10 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>Automated alerts for expiring documents, training, or licence renewals.</span>
                    </div>
                    <div className="howitworks-checklist-row">
                      <span className="howitworks-chevron">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 16L14 12L10 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>Corrective Action Register: assign owners, due dates, and track close-out.</span>
                    </div>
                    <div className="howitworks-checklist-row">
                      <span className="howitworks-chevron">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 16L14 12L10 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>Built-in project & task manager to track compliance projects from start to finish.</span>
                    </div>
                    <div className="howitworks-checklist-row">
                      <span className="howitworks-chevron">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 16L14 12L10 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>Photo evidence and audit findings linked directly to tasks and actions.</span>
                    </div>
                    <div className="howitworks-checklist-row">
                      <span className="howitworks-chevron">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 16L14 12L10 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>Export regulator-ready reports in PDF, Word, or Excel.</span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={12} md={6} className="mb-4 mb-md-0">
              <div className="howitworks-image-col text-center">
                <img src={mode === 'dark' ? DarkDashboard : LightDashboard} alt="Step 3" className="howitworks-image img-fluid" />
              </div>
            </Col>
          </Row>
          {/* Step 4 */}
          <Row className="align-items-center mb-5 flex-wrap">
            <Col xs={12} md={6} className="order-md-2">
              <div className="howitworks-step-info">
                <Badge className="howitworks-badge" bg="none">Step - 4</Badge>
                <div className="howitworks-step-content">
                  <div className="howitworks-step-title">Assign Roles & Responsibilities</div>
                  <div className="howitworks-step-desc">Make compliance accountability clear.</div>
                  <div className="howitworks-checklist">
                    <div className="howitworks-checklist-row">
                      <span className="howitworks-chevron">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 16L14 12L10 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>Create job descriptions and role profiles directly in the system.</span>
                    </div>
                    <div className="howitworks-checklist-row">
                      <span className="howitworks-chevron">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 16L14 12L10 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>Link roles to compliance tasks, training, and licence requirements.</span>
                    </div>
                    <div className="howitworks-checklist-row">
                      <span className="howitworks-chevron">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 16L14 12L10 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>Ensure every responsibility is assigned and trackable.</span>
                    </div>
                    <div className="howitworks-checklist-row">
                      <span className="howitworks-chevron">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 16L14 12L10 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>Aligns workers, supervisors, and management around compliance duties.</span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={12} md={6} className="order-md-1 mb-4 mb-md-0">
              <div className="howitworks-image-col text-center">
                <img src={mode === 'dark' ? DarkManageUser : LightManageUser} alt="Step 4" className="howitworks-image img-fluid" />
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
}

export default HowItWorks;
